<script lang="ts" setup>
import PageHeader from '@/components/header/PageHeader.vue'
import PageFooter from '@/components/footer/PageFooter.vue'
import ProjectList from '@/components/projects/ProjectList.vue'
import bus from '@/helper/Bus'
import { BusEvent, NavItem, NewsItem } from '@/helper/Types'
import { tagPageView } from '@/helper/analytics'
import { api } from '@/services/ApiService'
import { onMounted, onUnmounted, ref } from 'vue'
import router from '@/helper/router'
import i18n from '@/helper/Translations'
import OverviewNavBar from '@/components/OverviewNavBar.vue'
import NewsList from '@/components/news/NewsList.vue'
import NewsApi from '@/services/NewsApi'
import { UnreadNewsService } from '@/services/UnreadNewsService'
import userStore from '@/store/UserStore.ts'

const filterValue = ref('')
const activeTab = ref<NavItem>(NavItem.OVERVIEW)
const COMPONENT_NAME = 'ProjectOverviewPage'

setTitle()
bus.$onC(COMPONENT_NAME, BusEvent.LANGUAGE_CHANGED, setTitle)

const news = ref<NewsItem[]>([])

NewsApi.fetchNews()
  .then(n => (news.value = n))
  .catch(e => console.error('Error fetching news via API:', e))

onMounted((): void => {
  setTitle()
  tagPageView('/')
})

function getUnreadNewsCount(): number {
  return news.value.filter(n => UnreadNewsService.filterUnreadNews(n.id!)).length
}

async function createProjectClicked(): Promise<void> {
  await router.push({ name: 'project', params: { projectId: 'new' } })
}

async function createProjectFromJson() {
  const projectJson = prompt('Project JSON')

  if (projectJson) {
    await api.createProject(JSON.parse(projectJson))
    location.reload()
  }

  return
}

function setTitle(): void {
  document.title = `${i18n.global.t('overview.title')} - ${i18n.global.t('global.project_name')}`
}

onUnmounted((): void => {
  bus.$offC(COMPONENT_NAME)
})
</script>

<template>
  <div>
    <PageHeader />

    <div class="main-wrapper is--grey">
      <div class="container main">
        <div class="listing__header">
          <h1 class="listing__title">
            {{ $t('overview.title') }}
          </h1>

          <button v-if="userStore.isPrivileged" class="btn is--secondary listing__btn" @click="createProjectFromJson">
            Create project from JSON
          </button>

          <button class="btn listing__btn" tabindex="2" @click="createProjectClicked">
            {{ $t('overview.btn_create') }}
          </button>
        </div>

        <OverviewNavBar
          :show-tabs="news.length > 0"
          :unread-news-count="getUnreadNewsCount()"
          @filter-value-changed="changedValue => (filterValue = changedValue)"
          @set-active-tab="value => (activeTab = value)"
        />

        <ProjectList v-if="activeTab === NavItem.OVERVIEW" :filter-value="filterValue" />
        <NewsList v-else :news="news" />
      </div>
    </div>
    <PageFooter />
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.listing {
  &__header {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;

    @include media('>=desktop') {
      margin-bottom: 2rem;
    }
  }

  &__title {
    flex: 1;
    margin-bottom: 0;
    white-space: nowrap;
  }
}
</style>
